/**
 * Быстрые ответы планировщика (BE-072).
 *
 * Backend присылает и `label_key`, и готовую фразу `payload`. Интерфейс
 * использует **ключ**, а не фразу: `payload` собран планировщиком на одном
 * языке, и в немецком интерфейсе реплика пользователя в собственном чате
 * выглядела бы чужой. На извлечение это не влияет — locale уходит в LLM
 * отдельным полем (MVP_TECH_RFC §9).
 *
 * `payload` остаётся запасным вариантом: если ключа в словаре ещё нет, кнопка
 * всё равно осмысленна, а не показывает сырой `chat.answers.*`.
 */

import type { QuestionGroup, QuickReply, Translate } from '@entities/search-session';

export type HasKey = (key: string) => boolean;

/**
 * Сколько значений критерия имеет смысл выбрать.
 *
 * Приоритетов ровно три (MATCHING §4.3): четвёртый backend всё равно отбросит
 * с предупреждением, и честнее не дать его выбрать, чем принять и потерять.
 */
export const MAX_SELECTED_BY_SLOT: Record<string, number | undefined> = {
  priorities: 3,
};

/** Текст кнопки. Он же уходит сообщением — пользователь видит то, что отправил. */
export function quickReplyText(reply: QuickReply, t: Translate, hasKey: HasKey): string {
  return hasKey(reply.label_key) ? t(reply.label_key) : reply.payload;
}

/**
 * Новый набор выбранных кодов после нажатия кнопки.
 *
 * Порядок нажатий сохраняется: для приоритетов это порядок рангов, и
 * пересортировка молча поменяла бы смысл ответа. Внутри группы «неважно»
 * и конкретные значения взаимно исключают друг друга — вместе они
 * противоречивы, и разрешать такой выбор незачем.
 */
export function resolveSelection(
  selected: readonly string[],
  group: QuestionGroup,
  reply: QuickReply,
): string[] {
  const groupCodes = new Set(group.quick_replies.map((item) => item.code));
  const others = selected.filter((code) => !groupCodes.has(code));
  const inGroup = selected.filter((code) => groupCodes.has(code));

  if (inGroup.includes(reply.code)) {
    // Повторное нажатие снимает выбор: обязательную группу можно переиграть.
    return selected.filter((code) => code !== reply.code);
  }
  if (group.selection === 'single' || reply.exclusive) {
    return [...others, reply.code];
  }
  const exclusiveCodes = new Set(
    group.quick_replies.filter((item) => item.exclusive).map((item) => item.code),
  );
  return [...others, ...inGroup.filter((code) => !exclusiveCodes.has(code)), reply.code];
}

/*
 * Подсказка дописывает ответ в поле ввода (FE-595).
 *
 * Отправляет сообщение только сам человек — кнопкой отправки, как любое
 * другое. Нажатая подсказка добавляет свою подпись в черновик, повторное
 * нажатие её убирает, а всё набранное руками остаётся на месте: подсказку
 * можно дополнить своими словами («Kombi, aber mit Anhängerkupplung»).
 *
 * Подпись ищется **целым фрагментом между разделителями**, а не подстрокой:
 * «SUV» внутри «kein SUV-Modell» подсказкой не считается.
 */

/** Символы, которые могут стоять вокруг подписи в черновике. */
const BOUNDARY = /[\s,;.:!?()]/;

function labelIndex(text: string, label: string): number {
  if (label === '') return -1;
  let from = 0;
  for (;;) {
    const index = text.indexOf(label, from);
    if (index < 0) return -1;
    const before = index === 0 ? '' : text.charAt(index - 1);
    const after = text.charAt(index + label.length);
    if ((before === '' || BOUNDARY.test(before)) && (after === '' || BOUNDARY.test(after))) {
      return index;
    }
    from = index + 1;
  }
}

/** Стоит ли подпись в черновике отдельным фрагментом. */
export function containsLabel(text: string, label: string): boolean {
  return labelIndex(text, label) >= 0;
}

/** Дописать подпись в конец черновика через запятую. */
export function appendLabel(text: string, label: string): string {
  const head = text.trimEnd();
  if (head === '') return label;
  return /[,;:]$/.test(head) ? `${head} ${label}` : `${head}, ${label}`;
}

/** Убрать подпись из черновика вместе с её запятой; остальной текст не трогается. */
export function removeLabel(text: string, label: string): string {
  const index = labelIndex(text, label);
  if (index < 0) return text;
  let start = index;
  let end = index + label.length;
  const separatorBefore = /,\s*$/.exec(text.slice(0, start));
  const separatorAfter = /^\s*,\s*/.exec(text.slice(end));
  if (separatorBefore) {
    start -= separatorBefore[0].length;
  } else if (separatorAfter) {
    end += separatorAfter[0].length;
  }
  return (text.slice(0, start) + text.slice(end)).trim();
}

/**
 * Черновик после нажатия подсказки.
 *
 * `active` — подсказки, чьи подписи сейчас стоят в черновике; правила выбора
 * внутри группы те же, что раньше (`resolveSelection`): одиночный выбор
 * заменяет прежний вариант, «неважно» снимает конкретные значения.
 */
export function toggleInDraft(
  text: string,
  active: readonly string[],
  group: QuestionGroup,
  reply: QuickReply,
  labelOf: (code: string) => string | undefined,
): { text: string; codes: string[] } {
  const codes = resolveSelection(active, group, reply);
  let next = text;
  for (const code of active) {
    const label = labelOf(code);
    if (label !== undefined && !codes.includes(code)) next = removeLabel(next, label);
  }
  for (const code of codes) {
    const label = labelOf(code);
    if (label !== undefined && !active.includes(code)) next = appendLabel(next, label);
  }
  return { text: next, codes };
}
